import { useContext, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  BreadTransaction,
  CategoryType,
  FirestoreDocCategory,
} from "breadcommon";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";

export const TREND_SERIES = ["income", "expenses", "savings"] as const;
export type TrendSeries = (typeof TREND_SERIES)[number];

export interface TrendsMonth {
  /** "YYYY-MM", which also serves as the chart's index. */
  month: string;
  /** Each series' total for the month, positive in the usual direction. */
  totals: Record<TrendSeries, number>;
  /** The transactions behind each total, for drilldown. */
  transactions: Record<TrendSeries, BreadTransaction[]>;
  /** Unreviewed transactions in the month, which none of the totals count. */
  unreviewedCount: number;
  /** The month hasn't ended yet, so its totals are still growing. */
  inProgress: boolean;
}

export interface TrendsData {
  months: TrendsMonth[];
  /** Whether any month has a transaction to draw. */
  hasData: boolean;
  currency: string;
}

const SERIES_BY_CATEGORY_TYPE: Partial<Record<CategoryType, TrendSeries>> = {
  [CategoryType.Income]: "income",
  [CategoryType.Expense]: "expenses",
  [CategoryType.Savings]: "savings",
};

export function useTrendsData(beginDate: Dayjs, endDate: Dayjs): TrendsData {
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const preferences = useContext(PreferencesContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  const currency = preferences?.web?.default_currency ?? "USD";

  return useMemo(
    () =>
      buildTrendsData(
        convertTransactionsToSpecificCurrency(
          Array.from(transactions.values()),
          currency,
        ),
        categories,
        beginDate,
        endDate,
        currency,
        dayjs(),
      ),
    [
      transactions,
      categories,
      beginDate,
      endDate,
      currency,
      convertTransactionsToSpecificCurrency,
    ],
  );
}

/**
 * Totals income, expenses, and savings per month, from the first month of
 * `beginDate` through the month of `endDate`. Transactions are expected to have
 * already been converted into `currency`.
 *
 * Each total is the plain net of its categories' reviewed transactions for the
 * month: a refund reduces expenses, a clawback reduces income. Savings is
 * floored at zero, so a month of withdrawals shows no savings rather than
 * negative savings. Silent categories (transfers between the user's own
 * accounts) and uncategorized transactions belong to none of the three.
 *
 * Unreviewed transactions are left out of every total, since their category is
 * only tentative. Each month counts them instead, so the chart can say how
 * many it ignored.
 */
export function buildTrendsData(
  transactions: BreadTransaction[],
  categories: Map<string, FirestoreDocCategory>,
  beginDate: Dayjs,
  endDate: Dayjs,
  currency: string,
  today: Dayjs,
): TrendsData {
  const months = new Map<string, TrendsMonth>();
  const lastMonth = endDate.startOf("month");
  for (
    let month = beginDate.startOf("month");
    !month.isAfter(lastMonth);
    month = month.add(1, "month")
  ) {
    const key = month.format("YYYY-MM");
    months.set(key, {
      month: key,
      totals: { income: 0, expenses: 0, savings: 0 },
      transactions: { income: [], expenses: [], savings: [] },
      unreviewedCount: 0,
      inProgress: !month.endOf("month").isBefore(today, "day"),
    });
  }

  transactions.forEach((transaction) => {
    const month = months.get(transaction.date.format("YYYY-MM"));
    if (!month) return;

    if (!transaction.reviewed) {
      month.unreviewedCount++;
      return;
    }

    if (!transaction.categoryId) return;
    const category = categories.get(transaction.categoryId);
    if (!category) return;
    const series = SERIES_BY_CATEGORY_TYPE[category.type];
    if (!series) return;

    // Expense amounts are negative, so negating makes expenses positive.
    // Income and money moved into savings are already positive.
    month.totals[series] +=
      series === "expenses" ? -transaction.amount : transaction.amount;
    month.transactions[series].push(transaction);
  });

  const monthList = Array.from(months.values());
  monthList.forEach((month) => {
    month.totals.savings = Math.max(0, month.totals.savings);
  });

  return {
    months: monthList,
    hasData: monthList.some((month) =>
      TREND_SERIES.some((series) => month.transactions[series].length > 0),
    ),
    currency,
  };
}
